// ==============================|| PRESET THEME - BREW (as DEFAULT) ||============================== //

export default function Brew(colors, mode = "light") {
  const { grey } = colors;

  // Figma colors
  const base = {
    black: "#111111",
    white: "#FCFCFC",
    magenta: "#E5007E",
    gray: '#BEBEBE',
    gray2: "#E9E9E9",
    lightGray: "#F0F0F0",
  };

  const isLight = mode === "light";
  const contrastText = "#ffffff";

  // Primary / Secondary scales
  const primaryColors = [
    "#FDE6F2", // lighter
    "#F8B9DA", // 100
    "#F288C0", // 200
    "#EC56A5", // light
    "#E92B8E", // 400
    base.magenta, // main
    "#C00067", // dark
    "#9D2165", // BRW Magenta Dark
    "#9A0053", // 700
    "#74003F", // darker
    "#4E002B", // 900
  ];

  const secondaryColors = [
    "#F4F4F4", // lighter
    "#EAEAEA", // 100
    base.lightGray, // 200
    base.gray2, // light
    base.gray, // 400
    base.black, // main
    "#2E2E2E", // dark
    "#202020", // 700
    "#171717", // darker
    "#0F0F0F", // 900
  ];

  const errorColors = ["#FDE8E7", "#F25E52", "#F04134", "#EE3B2F", "#E92A21"];
  const warningColors = ["#FFF7E0", "#FFC926", "#FFBF00", "#FFB900", "#FFA900"];
  const infoColors = ["#E0F4F5", "#2E94FF", "#00A2AE", "#009AA7", "#008694"];
  const successColors = ["#E0F5EA", "#32a361", "#008d3a", "#007e34", "#00702e"];

  const backgroundDefault = isLight ? base.white : "#0F0F0F";
  const paperBg = isLight ? "#FFFFFF" : "#1A1A1A";

  const textPrimary = isLight ? base.black : base.white;
  const textSecondary = isLight ? "#3A3A3A" : "#CFCFCF";
  const textHint = isLight ? "#616161" : "#9E9E9E";

  return {
    ...colors,

    base: {
      black: base.black,
      white: base.white,
      magenta: base.magenta,
      gray: base.gray,
      gray2: base.gray2,
      lightGray: base.lightGray,
    },

    primary: {
      lighter: primaryColors[0],
      100: primaryColors[1],
      200: primaryColors[2],
      light: primaryColors[3],
      400: primaryColors[4],
      main: primaryColors[5],
      dark: primaryColors[6],
      700: primaryColors[7],
      darker: primaryColors[8],
      900: primaryColors[9],
      contrastText,
    },

    secondary: {
      lighter: secondaryColors[0],
      100: secondaryColors[1],
      200: secondaryColors[2],
      light: secondaryColors[3],
      400: secondaryColors[4],
      main: secondaryColors[5],
      dark: secondaryColors[6],
      700: secondaryColors[7],
      darker: secondaryColors[8],
      900: secondaryColors[9],
      contrastText,
    },

    error: {
      lighter: errorColors[0],
      light: errorColors[1],
      main: errorColors[2],
      dark: errorColors[3],
      darker: errorColors[4],
      contrastText,
    },

    warning: {
      lighter: warningColors[0],
      light: warningColors[1],
      main: warningColors[2],
      dark: warningColors[3],
      darker: warningColors[4],
      contrastText,
    },

    info: {
      lighter: infoColors[0],
      light: infoColors[1],
      main: infoColors[2],
      dark: infoColors[3],
      darker: infoColors[4],
      contrastText,
    },

    success: {
      lighter: successColors[0],
      light: successColors[1],
      main: successColors[2],
      dark: successColors[3],
      darker: successColors[4],
      contrastText,
    },

    background: {
      default: backgroundDefault,
      paper: paperBg,
    },

    common: {
      black: "#000000",
      white: "#ffffff",
    },

    text: {
      primary: textPrimary,
      secondary: textSecondary,
      hint: textHint,
      contrastText,
    },

    action: {
      hover: isLight ? "rgba(0,0,0,0.04)" : "rgba(255,255,255,0.06)",
      selected: isLight ? "rgba(229,0,126,0.12)" : "rgba(229,0,126,0.18)",
      disabled: grey[isLight ? 500 : 600],
      disabledBackground: isLight
        ? "rgba(0,0,0,0.08)"
        : "rgba(255,255,255,0.12)",
      focus: isLight ? "rgba(229,0,126,0.16)" : "rgba(229,0,126,0.24)",
      active: isLight ? "rgba(0,0,0,0.54)" : "rgba(255,255,255,0.7)",
    },

    divider: isLight ? base.gray2 : "#2A2A2A",

    gridColors: {
      borders: isLight ? "#e0e0e0" : "#2A2A2A",
      headerBackground: isLight ? "transparent" : "#121212",
      headerFont: isLight ? "#020700" : "#FFFFFF",
      headerSvg: isLight ? "#616161" : "#FFFFFF",
    },

    buttonColors: {
      containedMagenta: base.magenta,
      containedDarkMagenta: "#9D2165",
      containedColor: contrastText,
      containedHoverColor: contrastText,
    },

    pageTitle: {
      textColor: base.magenta,
      backgroundColor: "transparent",
      dividerColor: isLight ? "rgb(230, 233, 239)" : "#2A2A2A",
    },

    drawerBackgroundColor: isLight ? "#f9f9f9" : "#121212",
    drawerMenuBackgroundColor: isLight ? "#24292e" : "#0E1116",

    headerBackgroundColor: isLight ? "#134779" : "#0F0F0F",
    headerBackgroundGradientColor: isLight ? "#597ea1" : "#1E1E1E",

    inputPalette: {
      labelColor: isLight ? "#3474a4" : base.white,
      labelBackground: "transparent",
      inputColor: textPrimary,
      inputBackground: "transparent",
      disableUnderline: false,
      borderBottom: isLight ? "inherit" : `1px dotted ${base.gray}`,
    },

    formBox: {
      titleColor: isLight ? textPrimary : base.white,
      titleBackground: "transparent",
      dividerColor: isLight ? "#5cacd4" : base.magenta,
    },

    linkStyles: {
      color: base.magenta,
      hoverColor: infoColors[4],
    },
  };
}
